import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import {PersonSkills} from '@components/person-skills/PersonSkills';
import {GridNote} from '@components/grid/GridNote';
import {CoachingBestPracticesContent} from '@components/coaching-best-practices/CoachingBestPractices';
import User, {UserId} from 'records/User';
import ReactDOM from 'react-dom';
import {Moment} from 'moment';
import {List} from 'immutable';
import CoachingPlan, {PlanId} from 'records/CoachingPlan';
import {MetricData, RepChartPoint} from '@store/@types/metric';
import {DataPoint} from 'charts/types';
import {PlanSkillDiff} from 'components/dashboard/manager/nine-box/one-rep-chart/OneRepData.utils';

interface NineBoxSidebarProps {
  selectedDataPoint: RepChartPoint | undefined;
  startDate: Moment;
  endDate: Moment;
  onCreatePlan: () => void;
  meetingTime: MetricData;
  reviewTime: MetricData;
  adminId?: UserId;
  managerId?: UserId;
  repPlans: List<CoachingPlan>;
  skillChanges: List<PlanSkillDiff>;
  repDataLoading: boolean;
  planHighlight?: PlanId;
}

export const NineBoxSidebar: React.FC<NineBoxSidebarProps> = ({
  endDate,
  managerId,
  adminId,
  meetingTime,
  onCreatePlan,
  repPlans,
  reviewTime,
  selectedDataPoint,
  startDate,
  skillChanges,
  repDataLoading,
  planHighlight,
}) => {
  const sidebar = document.getElementById('right-sidebar');
  const right: Partial<React.ComponentProps<typeof Grid>> = {
    xs: sidebar ? 12 : 4,
    md: sidebar ? 12 : 3,
    item: true,
  };
  const selectedRep = selectedDataPoint?.rep;

  const {gridNote} = useStyles();

  const content = selectedRep ? (
    <PersonSkills
      {...right}
      hasPlan={repPlans.some(p => p.coach.id === managerId)}
      skillChanges={skillChanges}
      meetingTimeData={convertDataPoint(meetingTime, selectedRep)}
      reviewTimeData={convertDataPoint(reviewTime, selectedRep)}
      rep={selectedRep}
      from={startDate.toDate()}
      to={endDate.toDate()}
      managerId={managerId}
      adminId={adminId}
      onCreatePlan={onCreatePlan}
      isLoading={repDataLoading}
      planHighlight={planHighlight}
    />
  ) : (
    <GridNote {...right} className={gridNote} title="COACHING BEST PRACTICES">
      <CoachingBestPracticesContent />
    </GridNote>
  );

  /**
   * What's going on here?
   *
   * If `StatsPage2` deemed it necessary to render a sidebar, let's dump OUR sidebar content in there, rather
   * than trying to fight the layout.
   *
   * Otherwise, if there isn't a sidebar, we have plenty of space to render the sidebar right next to the grid,
   * because we're full width.
   *
   */
  if (sidebar) {
    return ReactDOM.createPortal(content, sidebar);
  } else {
    return content;
  }
};

const useStyles = makeStyles(theme => ({
  gridNote: {
    '& > div': {
      [theme.breakpoints.up('lg')]: {
        maxWidth: 280,
      },
    },
  },
}));

// This should not exist, there should not be two DataPoint types, please god kill DayJS
function convertDataPoint(metricData: MetricData, rep: User | undefined) {
  if (!rep) return [];
  const data = metricData[rep.id]?.data ?? [];
  return data
    .filter(d => d.value !== null)
    .map(d => ({
      value: d.value,
      date: d.date.toDate(),
    })) as DataPoint[];
}
